import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

const LegacyProjectRedirect = lazy(() => import("@/components/LegacyProjectRedirect"));
const LegacyNewsRedirect = lazy(() => import("@/components/LegacyNewsRedirect"));
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import SiteLayout from "@/components/site/SiteLayout";
import { AdminRoute, ProtectedRoute } from "@/components/auth/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";

// Lazy-load all non-landing routes to keep the initial JS bundle small.
// This in particular keeps Stripe.js and the embedded checkout SDK out of the
// homepage bundle — they are only fetched when the user visits /donate.
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const Placeholder = lazy(() => import("./pages/Placeholder.tsx"));
const AuthPage = lazy(() => import("./pages/Auth.tsx"));
const AuthMfa = lazy(() => import("./pages/AuthMfa.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Donate = lazy(() => import("./pages/Donate.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const ProjectsList = lazy(() => import("./pages/ProjectsList.tsx"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail.tsx"));
const FundraisersList = lazy(() => import("./pages/FundraisersList.tsx"));
const FundraiserDetail = lazy(() => import("./pages/FundraiserDetail.tsx"));
const StartFundraiser = lazy(() => import("./pages/StartFundraiser.tsx"));
const NewsList = lazy(() => import("./pages/NewsList.tsx"));
const NewsDetail = lazy(() => import("./pages/NewsDetail.tsx"));
const NewslettersList = lazy(() => import("./pages/NewslettersList.tsx"));
const NewsletterDetail = lazy(() => import("./pages/NewsletterDetail.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const TaxDeductibleDonationsSriLanka = lazy(
  () => import("./pages/guides/TaxDeductibleDonationsSriLanka.tsx"),
);

const Help = lazy(() => import("./pages/Help.tsx"));
const GetInvolved = lazy(() => import("./pages/GetInvolved.tsx"));
const MyAccount = lazy(() => import("./pages/MyAccount.tsx"));
const Transparency = lazy(() => import("./pages/Transparency.tsx"));
const MapPage = lazy(() => import("./pages/MapPage.tsx"));
const TheCircle = lazy(() => import("./pages/TheCircle.tsx"));
const Sponsor = lazy(() => import("./pages/Sponsor.tsx"));
const Birthday = lazy(() => import("./pages/Birthday.tsx"));
const ImpactSquad = lazy(() => import("./pages/ImpactSquad.tsx"));
const Partners = lazy(() => import("./pages/Partners.tsx"));
const Volunteer = lazy(() => import("./pages/Volunteer.tsx"));

const VolunteerOpportunityDetail = lazy(() => import("./pages/VolunteerOpportunityDetail.tsx"));
const DonorWall = lazy(() => import("./pages/DonorWall.tsx"));
const AiContext = lazy(() => import("./pages/AiContext.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminProjects = lazy(() => import("./pages/admin/AdminProjects.tsx"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories.tsx"));
const AdminHomepage = lazy(() => import("./pages/admin/AdminHomepage.tsx"));
const AdminStats = lazy(() => import("./pages/admin/AdminStats.tsx"));
const AdminNews = lazy(() => import("./pages/admin/AdminNews.tsx"));
const AdminNewsletters = lazy(() => import("./pages/admin/AdminNewsletters.tsx"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials.tsx"));
const AdminFundraisers = lazy(() => import("./pages/admin/AdminFundraisers.tsx"));
const AdminDonations = lazy(() => import("./pages/admin/AdminDonations.tsx"));
const AdminRecurring = lazy(() => import("./pages/admin/AdminRecurring.tsx"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers.tsx"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail.tsx"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings.tsx"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam.tsx"));
const AdminBirthdays = lazy(() => import("./pages/admin/AdminBirthdays.tsx"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners.tsx"));
const AdminVolunteers = lazy(() => import("./pages/admin/AdminVolunteers.tsx"));
const AdminOpportunities = lazy(() => import("./pages/admin/AdminOpportunities.tsx"));
const AdminContactMessages = lazy(() => import("./pages/admin/AdminContactMessages.tsx"));
const EventsList = lazy(() => import("./pages/EventsList.tsx"));
const EventDetail = lazy(() => import("./pages/EventDetail.tsx"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents.tsx"));
const AdminImpactSquad = lazy(() => import("./pages/admin/AdminImpactSquad.tsx"));
const AdminFaq = lazy(() => import("./pages/admin/AdminFaq.tsx"));
const AdminTransparency = lazy(() => import("./pages/admin/AdminTransparency.tsx"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog.tsx"));
const AdminRequests = lazy(() => import("./pages/admin/AdminRequests.tsx"));

// Generous staleTime so re-mounting Suspense-loaded components (e.g. when
// below-the-fold sections become visible) does not refire identical Supabase
// queries. This was the single biggest contributor to LCP/Speed Index on the
// homepage — the same 6 endpoints were being hit 2-3x per page load.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/projects" element={<ProjectsList />} />
                <Route path="/projects/:slug" element={<ProjectDetail />} />
                <Route path="/fundraisers" element={<FundraisersList />} />
                <Route path="/fundraisers/:slug" element={<FundraiserDetail />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/start-a-fundraiser" element={<StartFundraiser />} />
                </Route>
                <Route path="/donate" element={<Donate />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/news" element={<NewsList />} />
                <Route path="/news/:slug" element={<NewsDetail />} />
                <Route path="/newsletters" element={<NewslettersList />} />
                <Route path="/newsletters/:slug" element={<NewsletterDetail />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<Navigate to="/help#faq" replace />} />
                <Route path="/help" element={<Help />} />
                <Route path="/get-involved" element={<GetInvolved />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/auth/mfa" element={<AuthMfa />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/account" element={<MyAccount />} />
                </Route>
                <Route path="/transparency" element={<Transparency />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/the-circle" element={<TheCircle />} />
                <Route path="/sponsor" element={<Sponsor />} />
                <Route path="/birthday" element={<Birthday />} />
                <Route path="/little-helpers" element={<ImpactSquad />} />
                <Route path="/impact-squad" element={<ImpactSquad />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/volunteer" element={<Volunteer />} />
                <Route path="/volunteer/opportunities" element={<Navigate to="/volunteer" replace />} />
                <Route path="/volunteer/opportunities/:slug" element={<VolunteerOpportunityDetail />} />
                <Route path="/donor-wall" element={<DonorWall />} />
                <Route path="/events" element={<EventsList />} />
                <Route path="/events/:slug" element={<EventDetail />} />
                <Route path="/ai-context" element={<AiContext />} />
                <Route
                  path="/guides/tax-deductible-donations-sri-lanka"
                  element={<TaxDeductibleDonationsSriLanka />}
                />
              </Route>

              {/*
                Note: /admin (dashboard) intentionally uses ProtectedRoute (not AdminRoute) so a
                freshly-signed-in first user can call claim_first_admin to bootstrap the admin role.
                All sensitive data is protected at the database layer via RLS policies — non-admins
                see only the "claim first admin" / access-required screen.
              */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                </Route>
              </Route>
              <Route element={<AdminRoute />}>
                <Route element={<AdminLayout />}>
                  <Route path="/admin/projects" element={<AdminProjects />} />
                  <Route path="/admin/categories" element={<AdminCategories />} />
                  <Route path="/admin/homepage" element={<AdminHomepage />} />
                  <Route path="/admin/stats" element={<AdminStats />} />
                  <Route path="/admin/news" element={<AdminNews />} />
                  <Route path="/admin/newsletters" element={<AdminNewsletters />} />
                  <Route path="/admin/testimonials" element={<AdminTestimonials />} />
                  <Route path="/admin/team" element={<AdminTeam />} />
                  <Route path="/admin/fundraisers" element={<AdminFundraisers />} />
                  <Route path="/admin/donations" element={<AdminDonations />} />
                  <Route path="/admin/recurring" element={<AdminRecurring />} />
                  <Route path="/admin/birthdays" element={<AdminBirthdays />} />
                  <Route path="/admin/partners" element={<AdminPartners />} />
                  <Route path="/admin/requests" element={<AdminRequests />} />
                  <Route path="/admin/volunteers" element={<AdminVolunteers />} />
                  <Route path="/admin/volunteer-opportunities" element={<AdminOpportunities />} />
                  <Route path="/admin/contact" element={<AdminContactMessages />} />
                  <Route path="/admin/events" element={<AdminEvents />} />
                  <Route path="/admin/impact-squad" element={<AdminImpactSquad />} />
                  <Route path="/admin/faq" element={<AdminFaq />} />
                  <Route path="/admin/transparency" element={<AdminTransparency />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/users/:id" element={<AdminUserDetail />} />
                  <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />

                  <Route path="/admin/settings" element={<AdminSettings />} />
                </Route>
              </Route>

              {/* Legacy URL redirects — previous site used /our-projects/<category>/<slug>
                  and AMP variants. Without these, Google sees NotFound's noindex meta
                  and flags the old URLs. Slugs map 1:1 to current /projects/:slug. */}
              <Route path="/our-projects" element={<Navigate to="/projects" replace />} />
              <Route path="/our-projects/:category/:slug" element={<LegacyProjectRedirect />} />
              <Route path="/our-projects/:slug" element={<LegacyProjectRedirect />} />
              {/* Legacy Wix /projects/<category>/<slug> shape — current routes are slug-only. */}
              <Route path="/projects/:category/:slug" element={<LegacyProjectRedirect />} />
              <Route path="/amp/our-projects/:category/:slug" element={<LegacyProjectRedirect />} />
              <Route path="/amp/our-projects/:slug" element={<LegacyProjectRedirect />} />
              {/* Legacy /amp/news URLs flow to current /news article. */}
              <Route path="/amp/news" element={<Navigate to="/news" replace />} />
              <Route path="/amp/news/:slug" element={<LegacyNewsRedirect />} />
              <Route path="/amp/*" element={<Navigate to="/" replace />} />
              {/* Legacy Wix info pages indexed by Google. */}
              <Route path="/about/our-partners" element={<Navigate to="/partners" replace />} />
              <Route path="/about/testimonials/:slug" element={<Navigate to="/about" replace />} />
              <Route path="/how-we-do/make-impact" element={<Navigate to="/get-involved" replace />} />
              <Route path="/get-involved/monthly-membership" element={<Navigate to="/the-circle" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
